/* NK custom: find styles by number alone in the search drawer.
 *
 * Shopify's search only matches the start of a word, so "1305" can't find "SA1305". When the search box holds only
 * digits, this runs the theme's fast predictive search once per style prefix, in parallel ("1305", "SA1305",
 * "W1305", …), keeps the products whose style code contains the number, and shows them as one list.
 * Anything else typed (letters, words) goes through the theme's normal predictive search untouched.
 *
 * Prefixes come from the search drawer section setting "Style number prefixes" (data-style-prefixes).
 */
(() => {
  const PER_PREFIX = 6;   // results asked for per prefix
  const MAX_SHOWN = 10;   // results shown in the drawer
  let tries = 0;

  const isStyleNumber = (value) => /^\d{2,}$/.test(value.trim());
  const cache = new Map();

  async function fetchText(url, signal) {
    if (cache.has(url)) return cache.get(url);
    const text = await (await fetch(url, { signal })).text();
    cache.set(url, text);
    return text;
  }

  const styleCode = (node) => {
    const title = node.querySelector('.reversed-link')?.textContent || node.textContent || '';
    return title.trim().split(/\s+/)[0].toUpperCase();
  };

  function patch() {
    const PredictiveSearch = customElements.get('predictive-search');
    if (!PredictiveSearch) {
      if (++tries < 50) setTimeout(patch, 100);
      return;
    }
    if (PredictiveSearch.prototype.__nkStyleSearch) return;
    PredictiveSearch.prototype.__nkStyleSearch = true;

    const original = PredictiveSearch.prototype._doPredictiveSearch;

    PredictiveSearch.prototype._doPredictiveSearch = async function () {
      const value = this._queryInput.value.trim();
      const prefixes = (this.dataset.stylePrefixes || '').split(',').map((p) => p.trim().toUpperCase()).filter(Boolean);
      if (!isStyleNumber(value) || !prefixes.length) return original.call(this);

      // start the loading state and the requests at the same time (theme helpers when present)
      const loading = this._nkBeginLoading ? this._nkBeginLoading() : this._transitionToSlot('loading');
      const signal = this._abortController.signal;
      const sectionId = this.getAttribute('section-id');
      const terms = [value, ...prefixes.map((p) => p + value)];

      let docs;
      try {
        [docs] = await Promise.all([Promise.all(terms.map(async (term) => {
          const params = `q=${encodeURIComponent(term)}&section_id=${sectionId}&resources[type]=product&resources[limit]=${PER_PREFIX}&resources[limit_scope]=each`;
          const html = await fetchText(`${window.Shopify.routes.root}search/suggest?${params}`, signal);
          return new DOMParser().parseFromString(html, 'text/html');
        })), loading]);
      } catch (e) {
        if (e.name === 'AbortError') return; // a newer search replaced this one
        this.classList.remove('is-refreshing');
        return original.call(this);
      }
      if (this._queryInput.value.trim() !== value) return; // buyer kept typing

      // Collect matching products from every response, exact codes first, no duplicates
      const seen = new Set();
      const exact = [];
      const partial = [];
      const exactCodes = new Set(terms);
      docs.forEach((doc) => {
        doc.querySelectorAll('[slot="results"] .predictive-search-product').forEach((node) => {
          const href = (node.querySelector('a[href]')?.getAttribute('href') || '').split('?')[0];
          const code = styleCode(node);
          if (!href || seen.has(href) || !code.includes(value)) return;
          seen.add(href);
          (exactCodes.has(code) ? exact : partial).push(node);
        });
      });
      const matches = exact.concat(partial).slice(0, MAX_SHOWN);

      // Re-use one response's markup (tabs, empty state) so the drawer looks exactly like a normal search
      const withProducts = docs.find((d) => d.querySelector('[slot="results"] .predictive-search-product'));
      const base = (withProducts || docs[0]).querySelector('[slot="results"]');
      if (!base) { this.classList.remove('is-refreshing'); return original.call(this); }
      const results = document.importNode(base, true);

      if (matches.length) {
        const list = results.querySelector('.predictive-search-product').parentElement;
        // keep only the Products tab
        const tabs = list.parentElement;
        const titles = Array.from(tabs.querySelectorAll(':scope > .predictive-search__tab-item'));
        const panels = Array.from(tabs.querySelectorAll(':scope > .predictive-search__tab-content'));
        const productIndex = panels.indexOf(list);
        titles.forEach((t, i) => { if (i !== productIndex) t.remove(); });
        panels.forEach((p) => { if (p !== list) p.remove(); });
        list.replaceChildren(...matches.map((n) => document.importNode(n, true)));
      } else {
        const empty = docs.map((d) => d.querySelector('[slot="results"] .empty-state')).find(Boolean);
        results.replaceChildren(empty ? document.importNode(empty, true) : document.createTextNode(''));
      }

      if (this._nkShowResults) return this._nkShowResults(results);
      this.querySelector('[slot="results"]').replaceWith(results);
      return this._transitionToSlot('results');
    };

    // Enter on a bare number: the regular search page can't find "1305", so open the product when there's exactly
    // one match, otherwise keep the buyer in the drawer where the matches are listed.
    document.addEventListener('submit', (event) => {
      const form = event.target;
      const search = form.querySelector && form.querySelector('predictive-search');
      if (!search || !search.dataset.stylePrefixes) return;
      const input = form.elements['q'];
      if (!input || !isStyleNumber(input.value)) return;

      event.preventDefault();
      const links = search.querySelectorAll('[slot="results"] .predictive-search-product > a[href]');
      if (links.length === 1) window.location.href = links[0].href;
    }, true);
  }

  patch();
})();
